import { Card, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import Button from "../Button";
import { CircularProgressBar } from "../ProgressBar/Circular";
import type { ModuleArtifact } from "./types";

type Props = {
	artifact: ModuleArtifact;
	selected?: boolean;
	onPress: (artifact: ModuleArtifact) => void;
};

const ArtifactStatusRing = ({ artifact }: { artifact: ModuleArtifact }) => {
	const theme = useAppTheme();
	const isCompleted = artifact.status === "completed";
	const progress = isCompleted
		? 1
		: Math.min(Math.max(artifact.progressPercent ?? 0, 0), 100) / 100;
	const accentColor = isCompleted
		? theme.palette.success.dark
		: theme.colors.primary;

	return (
		<Box position="relative">
			<CircularProgressBar
				size="medium"
				progress={progress}
				progressColor={accentColor}
				trackColor={theme.colors.secondaryContainer}
			/>
			<Box
				position="absolute"
				top={0}
				left={0}
				right={0}
				bottom={0}
				alignItems="center"
				justifyContent="center"
			>
				<AppIcon
					name={isCompleted ? "check" : artifact.iconName}
					size={20}
					color={isCompleted ? accentColor : theme.colors.onSurfaceVariant}
				/>
			</Box>
		</Box>
	);
};

export const ModuleArtifactCard = ({
	artifact,
	selected = false,
	onPress,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const showsStatusRing =
		artifact.status === "completed" || artifact.status === "in-progress";
	const isActive =
		selected || artifact.status === "next" || artifact.status === "in-progress";
	const hidesButtonAction = isMobile && artifact.action?.mode !== "text";
	const action = hidesButtonAction ? undefined : artifact.action;
	const isLocked = artifact.status === "locked";
	const cardOnPress = isLocked || action ? undefined : () => onPress(artifact);

	return (
		<Card
			mode="outlined"
			onPress={cardOnPress}
			accessibilityState={{ disabled: isLocked }}
			style={{
				borderColor:
					isActive && !isMobile ? theme.colors.primary : theme.colors.surface,
				borderRadius: isMobile ? 0 : theme.roundness * 3,
				backgroundColor: theme.colors.surface,
			}}
		>
			<Card.Content style={{ paddingHorizontal: 16, paddingVertical: 12 }}>
				<Box
					flexDirection="row"
					alignItems={isMobile ? "flex-start" : "center"}
					gap={16}
				>
					{showsStatusRing ? (
						<ArtifactStatusRing artifact={artifact} />
					) : (
						<Box
							height={40}
							width={40}
							borderRadius={theme.roundness * 100}
							backgroundColor={theme.colors.secondaryContainer}
							alignItems="center"
							justifyContent="center"
						>
							<AppIcon
								name={artifact.iconName}
								size={24}
								color={theme.colors.onSecondaryContainer}
							/>
						</Box>
					)}
					<Box flex={1}>
						<Box flexDirection="row" alignItems="center" gap={8}>
							<Text
								variant="labelMedium"
								numberOfLines={1}
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{artifact.statusLabel}
							</Text>
							{artifact.creditLabel ? (
								<Box
									paddingHorizontal={8}
									paddingVertical={2}
									borderRadius={theme.roundness * 100}
									backgroundColor={theme.colors.elevation.level1}
								>
									<Text
										variant="labelMedium"
										style={{ color: theme.colors.primary }}
									>
										{artifact.creditLabel}
									</Text>
								</Box>
							) : null}
						</Box>
						<Text variant="bodyLarge" numberOfLines={2}>
							{artifact.title}
						</Text>
						<Text variant="bodyMedium" numberOfLines={3}>
							{artifact.description}
						</Text>
					</Box>
					{action ? (
						<Button
							mode={action.mode}
							icon={action.iconName}
							compact={action.mode === "text"}
							onPress={() => onPress(artifact)}
						>
							{action.label}
						</Button>
					) : null}
				</Box>
			</Card.Content>
		</Card>
	);
};
